import { ReactElement, useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import SettingsIcon from '@mui/icons-material/Settings'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import Divider from '@mui/material/Divider'
import axios from 'axios'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import httpClient from 'src/utilities/http-client'
import ImageUpload from 'src/v3/components/upload-image'
import {
  activeBookCleared,
  assignCategoryToBook,
  createChapter,
  deleteBook,
  deletedBookIDCleared,
  fetchBookByID,
  fetchBookCategories,
  fetchBookChapters,
  fetchCategories,
  removeCategoryFromBook,
  reorderChapters,
  republishBook,
  selectActiveBook,
  selectActiveBookStatus,
  selectBookCategories,
  selectBookCategoriesStatus,
  selectCategories,
  selectCategoriesStatus,
  selectChapters,
  selectChaptersStatus,
  selectDeleteBookStatus,
  selectDeletedBookID,
  selectRepublishBookStatus,
  selectUpdateBookStatus,
  updateBook,
  updatePublicationStatus,
} from '../../codex.slice'
import type { Category } from '../../types'
import CategoryManagerDialog from './category-manager-dialog'

let moveItem = (list: any[], oldIndex: number, newIndex: number) => {
  let without = list.filter((x, i) => i !== oldIndex)
  return [
    ...without.slice(0, newIndex),
    list[oldIndex],
    ...without.slice(newIndex),
  ]
}

let BookEditor = (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let { id } = useParams<{ id: string }>()

  let book = useAppSelector(selectActiveBook)
  let bookStatus = useAppSelector(selectActiveBookStatus)
  let updateBookStatus = useAppSelector(selectUpdateBookStatus)
  let deleteBookStatus = useAppSelector(selectDeleteBookStatus)
  let deletedBookID = useAppSelector(selectDeletedBookID)
  let republishBookStatus = useAppSelector(selectRepublishBookStatus)
  let chapters = useAppSelector(selectChapters)
  let chaptersStatus = useAppSelector(selectChaptersStatus)
  let categories = useAppSelector(selectCategories)
  let categoriesStatus = useAppSelector(selectCategoriesStatus)
  let bookCategories = useAppSelector(selectBookCategories)
  let bookCategoriesStatus = useAppSelector(selectBookCategoriesStatus)

  let [settingsOpen, setSettingsOpen] = useState(false)
  let [title, setTitle] = useState('')
  let [excerpt, setExcerpt] = useState('')
  let [displayVersion, setDisplayVersion] = useState('')
  let [hasChanges, setHasChanges] = useState(false)
  let [thumbnail, setThumbnail] = useState<File | undefined>(undefined)
  let [existingThumbnail, setExistingThumbnail] = useState<string>('')
  let [isSavingThumbnail, setIsSavingThumbnail] = useState(false)
  let [isAddingChapter, setIsAddingChapter] = useState(false)
  let [categoryManagerOpen, setCategoryManagerOpen] = useState(false)

  let autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null)
  let titleRef = useRef(title)
  let excerptRef = useRef(excerpt)
  let displayVersionRef = useRef(displayVersion)

  useEffect(() => {
    if (id) {
      dispatch(fetchBookByID(id))
      dispatch(fetchBookChapters(id))
    }

    return () => {
      dispatch(activeBookCleared())
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current)
      }
    }
  }, [id, dispatch])

  useEffect(() => {
    if (book) {
      setTitle(book.title)
      setExcerpt(book.excerpt || '')
      setExistingThumbnail(book.thumbnails || '')
      setDisplayVersion(
        book.displayVersion === null || book.displayVersion === undefined
          ? ''
          : String(book.displayVersion),
      )
      setHasChanges(false)
      // eslint-disable-next-line fp/no-mutation
      titleRef.current = book.title
      // eslint-disable-next-line fp/no-mutation
      excerptRef.current = book.excerpt || ''
      // eslint-disable-next-line fp/no-mutation
      displayVersionRef.current =
        book.displayVersion === null || book.displayVersion === undefined
          ? ''
          : String(book.displayVersion)
    }
  }, [book])

  useEffect(() => {
    // eslint-disable-next-line fp/no-mutation
    titleRef.current = title
  }, [title])

  useEffect(() => {
    // eslint-disable-next-line fp/no-mutation
    excerptRef.current = excerpt
  }, [excerpt])

  useEffect(() => {
    // eslint-disable-next-line fp/no-mutation
    displayVersionRef.current = displayVersion
  }, [displayVersion])

  useEffect(() => {
    if (deletedBookID === id) {
      navigate('/codex/books')
      dispatch(deletedBookIDCleared())
    }
  }, [deletedBookID, id, navigate, dispatch])

  let handleBackToBooks = () => {
    navigate('/codex/books')
  }

  let handleDeleteBook = () => {
    if (!id) return
    dispatch(deleteBook(id))
  }

  let saveBook = useCallback(() => {
    if (!id) return

    dispatch(
      updateBook({
        id,
        data: {
          title: titleRef.current,
          excerpt: excerptRef.current || undefined,
          displayVersion: displayVersionRef.current
            ? Number(displayVersionRef.current)
            : undefined,
        },
      }),
    )
    setHasChanges(false)
  }, [id, dispatch])

  let scheduleAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current)
    }

    // eslint-disable-next-line fp/no-mutation
    autoSaveTimerRef.current = setTimeout(() => {
      saveBook()
    }, 2000)
  }, [saveBook])

  let handleTitleChange = (value: string) => {
    setTitle(value)
    setHasChanges(true)
    scheduleAutoSave()
  }

  let handleExcerptChange = (value: string) => {
    setExcerpt(value)
    setHasChanges(true)
    scheduleAutoSave()
  }

  let handleDisplayVersionChange = (value: string) => {
    setDisplayVersion(value.replace(/[^\d]/g, ''))
    setHasChanges(true)
    scheduleAutoSave()
  }

  let uploadThumbnail = useCallback(
    async (file: File) => {
      if (!id) return

      setIsSavingThumbnail(true)
      try {
        await httpClient
          .put(`/api/codex/books/${id}/thumbnail`)
          .then((response) =>
            axios.put(response.data, file, {
              headers: {
                'Cache-Control': 'max-age=2592000',
                'content-type': 'image/jpeg',
              },
            }),
          )
        dispatch(fetchBookByID(id))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Thumbnail upload failed:', error)
      } finally {
        setIsSavingThumbnail(false)
      }
    },
    [id, dispatch],
  )

  useEffect(() => {
    if (thumbnail && id) {
      uploadThumbnail(thumbnail)
    }
  }, [thumbnail, id, uploadThumbnail])

  let handleManualSave = () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current)
    }
    saveBook()
  }

  let handleAddChapter = async () => {
    if (!id) return

    setIsAddingChapter(true)
    try {
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(
        createChapter({
          bookID: id,
          data: {
            title: 'Untitled',
          },
        }),
      )
      dispatch(fetchBookChapters(id))
    } finally {
      setIsAddingChapter(false)
    }
  }

  let handleChapterClick = (chapterID: string) => {
    navigate(`/codex/books/${id}/chapters/${chapterID}`)
  }

  let handleSettingsOpen = () => {
    setSettingsOpen(true)
    if (id) {
      dispatch(fetchBookCategories(id))
    }
    if (categories.length === 0) {
      dispatch(fetchCategories())
    }
  }

  let selectedCategory = bookCategories[0] ?? null

  let handleCategoryChange = (newValue: Category | null) => {
    if (!id) return

    bookCategories
      .filter((c) => c.id !== newValue?.id)
      .forEach((c) => {
        dispatch(removeCategoryFromBook({ bookID: id, categoryID: c.id }))
      })

    if (newValue && newValue.id !== selectedCategory?.id) {
      dispatch(
        assignCategoryToBook({ bookID: id, data: { categoryID: newValue.id } }),
      )
    }
  }

  let handleDragEnd = (result: any) => {
    if (!result.destination || !id) {
      return
    }

    let reorderedChapters = moveItem(
      chapters,
      result.source.index,
      result.destination.index,
    )

    dispatch(
      reorderChapters({
        bookID: id,
        data: {
          chapters: reorderedChapters.map((chapter, index) => ({
            id: chapter.id,
            sequence: index + 1,
          })),
        },
      }),
    )
  }

  let isLoading = bookStatus === 'pending'
  let isSaving = updateBookStatus === 'pending'
  let isDeleting = deleteBookStatus === 'pending'
  let isRepublishing = republishBookStatus === 'pending'

  let saveButtonText = isSaving ? 'Saving...' : 'Save'
  let hasPendingChanges = hasChanges

  return (
    <>
      <Container maxWidth="xl" className="py-6">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Button startIcon={<ArrowBackIcon />} onClick={handleBackToBooks}>
            Books
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={
                isSaving ? <CircularProgress size={16} /> : <SaveIcon />
              }
              onClick={handleManualSave}
              disabled={!hasPendingChanges || isSaving}
            >
              {saveButtonText}
            </Button>
            <IconButton onClick={handleSettingsOpen}>
              <SettingsIcon />
            </IconButton>
          </Box>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {book && (
          <Box>
            <TextField
              fullWidth
              variant="standard"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Book Title"
              sx={{ mb: 2 }}
              slotProps={{
                input: {
                  disableUnderline: true,
                  sx: {
                    fontSize: '2.5rem',
                    fontWeight: 'bold',
                    '& input': {
                      padding: 0,
                    },
                  },
                },
              }}
            />
            <TextField
              fullWidth
              multiline
              variant="standard"
              value={excerpt}
              onChange={(e) => handleExcerptChange(e.target.value)}
              placeholder="Write an excerpt..."
              sx={{ mb: 4 }}
              slotProps={{
                input: {
                  disableUnderline: true,
                  sx: {
                    fontSize: '1.1rem',
                    fontStyle: 'italic',
                    color: 'text.secondary',
                    '& textarea': {
                      padding: 0,
                    },
                  },
                },
              }}
            />
            <Box>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                  mb: 2,
                }}
                onClick={handleAddChapter}
              >
                {isAddingChapter ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    + Add Chapter
                  </Typography>
                )}
              </Box>

              {chaptersStatus === 'pending' && chapters.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {chapters.length === 0 && chaptersStatus !== 'pending' && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No chapters yet. Click above to add your first chapter.
                  </Typography>
                </Box>
              )}

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="chapters-list">
                  {(provided) => (
                    <Box
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}
                    >
                      {chapters.map((chapter, index) => (
                        <Draggable
                          key={chapter.id}
                          draggableId={chapter.id}
                          index={index}
                        >
                          {(prov, snapshot) => (
                            <Paper
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                cursor: snapshot.isDragging
                                  ? 'grabbing'
                                  : 'grab',
                                borderRadius: 0,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                bgcolor: snapshot.isDragging
                                  ? 'action.selected'
                                  : 'background.paper',
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                },
                                '&:first-of-type': {
                                  borderTopLeftRadius: 1,
                                  borderTopRightRadius: 1,
                                },
                                '&:last-of-type': {
                                  borderBottomLeftRadius: 1,
                                  borderBottomRightRadius: 1,
                                  borderBottom: 'none',
                                },
                              }}
                            >
                              <Box
                                {...prov.dragHandleProps}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  color: 'text.secondary',
                                  cursor: 'grab',
                                  '&:active': {
                                    cursor: 'grabbing',
                                  },
                                }}
                              >
                                <DragIndicatorIcon />
                              </Box>

                              <Box
                                sx={{ flex: 1, minWidth: 0 }}
                                onClick={() => handleChapterClick(chapter.id)}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mb: 0.5, display: 'block' }}
                                >
                                  Chapter {index + 1}
                                </Typography>
                                <Typography variant="h6" sx={{ mb: 0.5 }}>
                                  {chapter.title}
                                </Typography>
                                {chapter.subtitle && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {chapter.subtitle}
                                  </Typography>
                                )}
                              </Box>
                            </Paper>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
          </Box>
        )}
      </Container>
      <Drawer
        anchor="right"
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              p: 3,
              bgcolor: 'background.default',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h6">Book Settings</Typography>
            <IconButton onClick={() => setSettingsOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Excerpt
            </Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={excerpt}
              onChange={(e) => handleExcerptChange(e.target.value)}
              placeholder="Write an excerpt..."
              sx={{ bgcolor: 'background.paperDark' }}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Display Version
            </Typography>
            <TextField
              size="small"
              fullWidth
              type="number"
              value={displayVersion}
              onChange={(e) => handleDisplayVersionChange(e.target.value)}
              placeholder="e.g. 1"
              sx={{ bgcolor: 'background.paperDark' }}
              slotProps={{ htmlInput: { min: 0, step: 1 } }}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
            >
              <Typography variant="subtitle2">Thumbnail</Typography>
              {isSavingThumbnail && (
                <Typography variant="caption" color="text.secondary">
                  (Uploading...)
                </Typography>
              )}
            </Box>
            <ImageUpload
              id="book-thumbnail"
              type="2/3"
              image={thumbnail}
              setImage={setThumbnail}
              existingImage={existingThumbnail}
              sxOverride={{
                backgroundColor: 'background.paperDark',
                borderColor: 'divider',
              }}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle2">Categories</Typography>
              <Link
                component="button"
                variant="caption"
                underline="hover"
                onClick={() => setCategoryManagerOpen(true)}
              >
                Manage
              </Link>
            </Box>
            <Autocomplete
              size="small"
              loading={categoriesStatus === 'pending'}
              options={categories}
              getOptionLabel={(c) => c.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={selectedCategory}
              onChange={(_, newValue) => handleCategoryChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    selectedCategory ? undefined : 'Select a category...'
                  }
                  sx={{ bgcolor: 'background.paperDark' }}
                />
              )}
            />
            {bookCategoriesStatus !== 'pending' && categories.length === 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                No categories yet.{' '}
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => setCategoryManagerOpen(true)}
                >
                  Create one.
                </Link>
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Publication
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5 }}
            >
              {book?.publicationStatus === 'published'
                ? 'This book is published. Chapters can be published individually.'
                : 'Publish this book to allow individual chapters to be published.'}
            </Typography>
            {book?.publicationStatus === 'published' && (
              <Button
                fullWidth
                variant="contained"
                startIcon={
                  isRepublishing ? <CircularProgress size={16} /> : undefined
                }
                disabled={isRepublishing}
                onClick={() => {
                  if (!id) return
                  dispatch(republishBook(id))
                }}
                sx={{ mb: 1.5 }}
              >
                {isRepublishing ? 'Republishing...' : 'Republish Book'}
              </Button>
            )}
            <Button
              fullWidth
              variant={
                book?.publicationStatus === 'published'
                  ? 'outlined'
                  : 'contained'
              }
              onClick={() => {
                if (!id || !book) return
                dispatch(
                  updatePublicationStatus({
                    id,
                    data: {
                      publicationStatus:
                        book.publicationStatus === 'published'
                          ? 'unpublished'
                          : 'published',
                    },
                  }),
                )
              }}
            >
              {book?.publicationStatus === 'published'
                ? 'Unpublish Book'
                : 'Publish Book'}
            </Button>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            color="error"
            startIcon={
              isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />
            }
            onClick={handleDeleteBook}
            disabled={isDeleting}
            fullWidth
          >
            Delete Book
          </Button>
        </Box>
      </Drawer>
      <CategoryManagerDialog
        open={categoryManagerOpen}
        onClose={() => setCategoryManagerOpen(false)}
      />
    </>
  )
}

export default BookEditor
